import React, { useEffect, useRef, useState } from 'react'
import { Card, Typography, Table, TableBody, TableRow, TableContainer, Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { BasicSystemResponseModel, ProductData } from 'src/Libs/shop/models/basic-system.model'
import { ShopMainSkeleton } from 'src/Libs/shop'
import useSelectedUserRoutes from 'src/Hooks/useSelectedUserRoutes'
import { useNavigate } from 'react-router-dom'
import { getImageUrlBySize } from 'src/Libs/shop/utils'
import { ShopImageThumnbailSizes, ShopSystemConfigurationTypes } from 'src/Libs/shop/types'
import { usePageActionsContext } from 'src/Libs/header/context'
import { BasicSystemTableHeader, BasicSystemTablePagination, BasicSystemTableProduct } from 'src/Libs/shop/components/SystemConfiguration'
import { TotalMonths } from 'src/Libs/shop/utils/basic-system-fix-flex'
import useWindowSize from '@e3dc-react/shell/Hooks/useWindowSize'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

interface BasicSystemMonthCellComponentProps {
  isCellHeaderEmpty: boolean
  month: ProductData
  existingSystemConfiguratorShippingDate: Date | null
  cartSystemConfigurationIds: string[] | undefined
  existingSystemConfigurationType?: ShopSystemConfigurationTypes
  productData: BasicSystemResponseModel
  handleBasicSystemConfigurationChange: (
    selectedConfigurator: string | null,
    productName: string,
    productId: string,
    productYear: string,
    productMonth: string
  ) => void
}

interface BasicSystemProps {
  title: string
  onBack: () => void
  isLoading: boolean
  totalMonths: TotalMonths[]
  totalProducts: ProductData[]
  productData: BasicSystemResponseModel | null
  existingSystemConfiguratorShippingDate: Date | null
  cartSystemConfigurationIds: string[] | undefined
  existingSystemConfigurationType?: ShopSystemConfigurationTypes
  handleBasicSystemConfigurationChange: (
    selectedConfigurator: string | null,
    productName: string,
    productId: string,
    productYear: string,
    productMonth: string
  ) => void
  MonthCellComponent: React.ComponentType<BasicSystemMonthCellComponentProps>
}

const useStyles = makeStyles()((theme, props) => ({
  basicSystemContainer: {
    marginBottom: theme.spacing(10),
    paddingBottom: theme.spacing(10),
  },
  title: {
    fontSize: '32px',
    fontWeight: 400,
    marginBottom: theme.spacing(2),
    paddingRight: theme.spacing(4),
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.shade04,
      border: `1px solid ${theme.palette.shades.shade03}`,
    },
    '&:nth-of-type(even)': {
      backgroundColor: 'transparent',
    },
  },
}))

const CHILD_WIDTH = 180
const MIN_NUM_TIMES_FIT = 1

/*
 * Basic System table rendering products, months, and custom month cell component
 */
const BasicSystem: React.FC<BasicSystemProps> = ({
  title,
  onBack,
  productData,
  isLoading,
  totalMonths,
  totalProducts,
  existingSystemConfigurationType,
  cartSystemConfigurationIds,
  existingSystemConfiguratorShippingDate,
  handleBasicSystemConfigurationChange,
  MonthCellComponent,
}) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const navigate = useNavigate()
  const { dispatchPageActions } = usePageActionsContext()
  const { width, height } = useWindowSize()
  const parentRef = useRef<HTMLDivElement>(null)
  const [numTimesFit, setNumTimesFit] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState(0)

  const validNumTimesFit = Math.max(1, Math.floor(numTimesFit))
  const numFits = Math.ceil(totalMonths.length / validNumTimesFit)
  const calculatedNavigations: number[] = numFits > 0 ? Array.from({ length: numFits }) : []
  const itemsPerPage = numTimesFit
  const startIndex = currentPage * itemsPerPage
  const endIndex = startIndex + itemsPerPage

  useSelectedUserRoutes(t('shop.system-configure.basic-system-title'))

  useEffect(() => {
    dispatchPageActions?.([
      {
        position: 'left',
        type: 'back',
        onClick: onBack,
      },
    ])

    return () => {
      dispatchPageActions?.([])
    }
  }, [dispatchPageActions, navigate, onBack])

  useEffect(() => {
    if (parentRef.current) {
      const calculatedNumTimesFit = Math.floor((parentRef.current.offsetWidth - 200) / CHILD_WIDTH)
      const nextNumTimesFit = Math.max(calculatedNumTimesFit, MIN_NUM_TIMES_FIT)
      setNumTimesFit(nextNumTimesFit)
    }
  }, [productData, currentPage, width, height])

  return (
    <Box className={classes.basicSystemContainer}>
      <Typography className={classes.title}>{title}</Typography>

      {isLoading ? (
        <Box marginTop={3}>
          <ShopMainSkeleton shopSkeletonType="product" />
        </Box>
      ) : (
        <Card ref={parentRef} sx={{ marginBottom: 2, padding: 0 }}>
          <TableContainer>
            <Table>
              <BasicSystemTableHeader
                totalMonths={totalMonths}
                firstPage={startIndex}
                lastPage={endIndex}
                existingSystemConfiguratorShippingDate={existingSystemConfiguratorShippingDate}
              />

              <TableBody>
                {productData?.elements.map((product, itemId) => {
                  const filteredProducts = totalProducts.filter(products => products.productNumber === itemId)
                  const numColumns = endIndex - startIndex
                  const defaultData = new Array(numColumns).fill(0)
                  const productImageUrl = getImageUrlBySize(product.mainImage?.thumbnails, ShopImageThumnbailSizes.ExtraSmall)

                  return (
                    <TableRow key={product.id} className={classes.tableRow}>
                      <BasicSystemTableProduct
                        productImage={productImageUrl}
                        productName={product.translated.name}
                        productDescription={product.description}
                      />

                      {defaultData.map((_, columnIndex) => {
                        const monthIndex = startIndex + columnIndex
                        const month = filteredProducts[monthIndex]
                        const headerCellIsEmpty = !totalMonths[monthIndex]

                        return (
                          <MonthCellComponent
                            key={monthIndex}
                            isCellHeaderEmpty={headerCellIsEmpty}
                            month={month}
                            cartSystemConfigurationIds={cartSystemConfigurationIds}
                            existingSystemConfiguratorShippingDate={existingSystemConfiguratorShippingDate}
                            existingSystemConfigurationType={existingSystemConfigurationType}
                            productData={productData}
                            handleBasicSystemConfigurationChange={handleBasicSystemConfigurationChange}
                          />
                        )
                      })}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box className={classes.paginationContainer}>
            <BasicSystemTablePagination
              pages={calculatedNavigations}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              lastPage={endIndex}
              totalMonths={totalMonths}
            />
          </Box>
        </Card>
      )}
    </Box>
  )
}

export default BasicSystem
